import React, { useState, useEffect } from 'react';
import { Responsive, WidthProvider } from 'react-grid-layout';
import { toast } from 'react-toastify';
import { format, subDays } from 'date-fns';
import {
    TrendingUp, Calendar, DollarSign, Users,
    BarChart3, RefreshCw
} from 'lucide-react';
import analyticsService from '../../services/analyticsService';
import StatsCard from './StatsCard';
import { BookingsTimelineChart, BookingsByEventChart, PaymentMethodsChart } from './BookingsCharts';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

const ResponsiveGridLayout = WidthProvider(Responsive);

const BookingsDashboard = () => {
    const [loading, setLoading] = useState(true);
    const [analytics, setAnalytics] = useState(null);
    const [eventsList, setEventsList] = useState([]);

    // Filters
    const [filters, setFilters] = useState({
        startDate: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
        endDate: format(new Date(), 'yyyy-MM-dd'),
        eventId: ''
    });

    // Grid layout configuration
    const layouts = {
        lg: [
            { i: 'total-bookings', x: 0, y: 0, w: 3, h: 3, static: true },
            { i: 'today-bookings', x: 3, y: 0, w: 3, h: 3, static: true },
            { i: 'revenue', x: 6, y: 0, w: 3, h: 3, static: true },
            { i: 'avg-order', x: 9, y: 0, w: 3, h: 3, static: true },
            { i: 'timeline', x: 0, y: 3, w: 8, h: 6, isResizable: true, isDraggable: false, minW: 4, minH: 5 },
            { i: 'by-event', x: 8, y: 3, w: 4, h: 6, isResizable: true, isDraggable: false, minW: 3, minH: 5 },
            { i: 'payment-methods', x: 0, y: 9, w: 4, h: 6, isResizable: true, isDraggable: false, minW: 3, minH: 5 },
            { i: 'recent', x: 4, y: 9, w: 8, h: 6, isResizable: false, isDraggable: false }
        ]
    };

    // Fetch analytics data
    useEffect(() => {
        fetchAnalytics();
        fetchEventsList();
    }, []);

    const fetchAnalytics = async () => {
        try {
            setLoading(true);
            const response = await analyticsService.getBookingAnalytics(filters);
            if (response.success) {
                setAnalytics(response.data);
            }
        } catch (error) {
            toast.error('Failed to load analytics');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const fetchEventsList = async () => {
        try {
            const response = await analyticsService.getEventsList();
            if (response.success) {
                setEventsList(response.data);
            }
        } catch (error) {
            console.error('Failed to load events:', error);
        }
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    const applyFilters = () => {
        fetchAnalytics();
    };

    if (loading) {
        return (
            <div className="flex flex-col justify-center items-center h-screen bg-gray-50">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600"></div>
                <p className="mt-4 text-lg text-gray-600">Loading analytics...</p>
            </div>
        );
    }

    if (!analytics) {
        return (
            <div className="flex flex-col justify-center items-center h-screen bg-gray-50">
                <BarChart3 className="w-16 h-16 text-gray-400 mb-4" />
                <p className="text-xl text-gray-600">No data available</p>
                <button
                    onClick={fetchAnalytics}
                    className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                >
                    Retry
                </button>
            </div>
        );
    }

    const { summary, timeline, byEvent, paymentMethods, recentBookings } = analytics;

    return (
        <div className="bookings-dashboard min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white shadow-sm border-b px-8 py-6 mb-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                            <BarChart3 className="w-8 h-8 text-blue-600" />
                            Bookings Analytics
                        </h1>
                        <p className="text-gray-600 mt-1">Monitor and analyze your booking performance</p>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="mx-8 mb-6">
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl shadow-sm p-6 border border-blue-100">
                    <div className="flex flex-wrap gap-4 items-end">
                        <div className="flex-1 min-w-[200px]">
                            <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                                <Calendar className="w-4 h-4" />
                                Start Date
                            </label>
                            <input
                                type="date"
                                value={filters.startDate}
                                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                            />
                        </div>

                        <div className="flex-1 min-w-[200px]">
                            <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                                <Calendar className="w-4 h-4" />
                                End Date
                            </label>
                            <input
                                type="date"
                                value={filters.endDate}
                                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                            />
                        </div>

                        <div className="flex-1 min-w-[250px]">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Event
                            </label>
                            <select
                                value={filters.eventId}
                                onChange={(e) => handleFilterChange('eventId', e.target.value)}
                                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                            >
                                <option value="">All Events</option>
                                {eventsList.map(event => (
                                    <option key={event._id} value={event._id}>
                                        {event.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <button
                            onClick={applyFilters}
                            className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all shadow-md hover:shadow-lg flex items-center gap-2 font-medium"
                        >
                            <RefreshCw className="w-4 h-4" />
                            Apply Filters
                        </button>
                    </div>
                </div>
            </div>

            {/* Grid Layout */}
            <div className="px-8">
                <ResponsiveGridLayout
                    className="layout"
                    layouts={layouts}
                    breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
                    cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
                    rowHeight={40}
                    isDraggable={false}
                    isResizable={true}
                >
                    {/* Stats Cards */}
                    <div key="total-bookings">
                        <StatsCard
                            title="Total Bookings"
                            value={summary.totalBookings}
                            subtitle={`${summary.confirmedBookings} confirmed, ${summary.pendingBookings} pending`}
                            icon={<Users className="w-6 h-6" />}
                            color="blue"
                        />
                    </div>

                    <div key="today-bookings">
                        <StatsCard
                            title="Today's Bookings"
                            value={summary.todayBookings}
                            subtitle="Bookings received today"
                            icon={<Calendar className="w-6 h-6" />}
                            color="green"
                        />
                    </div>

                    <div key="revenue">
                        <StatsCard
                            title="Total Revenue"
                            value={`₹${summary.totalRevenue.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                            subtitle="Total earnings"
                            icon={<DollarSign className="w-6 h-6" />}
                            color="purple"
                        />
                    </div>

                    <div key="avg-order">
                        <StatsCard
                            title="Avg Order Value"
                            value={`₹${summary.averageOrderValue.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                            subtitle="Per booking"
                            icon={<TrendingUp className="w-6 h-6" />}
                            color="orange"
                        />
                    </div>

                    {/* Charts */}
                    <div key="timeline">
                        <div className="h-full">
                            <BookingsTimelineChart data={timeline} />
                        </div>
                    </div>

                    <div key="by-event">
                        <div className="h-full">
                            <BookingsByEventChart data={byEvent} />
                        </div>
                    </div>

                    <div key="payment-methods">
                        <div className="h-full">
                            <PaymentMethodsChart data={paymentMethods} />
                        </div>
                    </div>

                    {/* Recent Bookings Table */}
                    <div key="recent">
                        <div className="bg-white rounded-xl shadow-sm p-6 h-full border border-gray-200 hover:shadow-md transition-shadow">
                            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                                <BarChart3 className="w-5 h-5 text-blue-600" />
                                Recent Bookings
                            </h3>
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Ticket #</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Event</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">User</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Amount</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {recentBookings && recentBookings.length > 0 ? (
                                            recentBookings.map(booking => (
                                                <tr key={booking._id} className="hover:bg-gray-50 transition-colors">
                                                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{booking.ticketNumber || 'N/A'}</td>
                                                    <td className="px-4 py-3 text-sm text-gray-700">{booking.event?.name || 'N/A'}</td>
                                                    <td className="px-4 py-3 text-sm text-gray-700">{booking.user?.name || 'N/A'}</td>
                                                    <td className="px-4 py-3 text-sm font-semibold text-gray-900">₹{booking.finalAmount}</td>
                                                    <td className="px-4 py-3 text-sm">
                                                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${booking.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                                                            booking.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                                                'bg-red-100 text-red-800'
                                                            }`}>
                                                            {booking.status.toUpperCase()}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                                                    No bookings found for the selected period
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </ResponsiveGridLayout>
            </div>
        </div>
    );
};

export default BookingsDashboard;
